'use client'

import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'

export function beep() {
  try {
    const ctx = new AudioContext()
    // Beep style caisse magasin — fréquence haute, court, net
    const t = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain); gain.connect(ctx.destination)
    osc.type = 'square'
    osc.frequency.setValueAtTime(1800, t)
    osc.frequency.exponentialRampToValueAtTime(1600, t + 0.06)
    gain.gain.setValueAtTime(0.0, t)
    gain.gain.linearRampToValueAtTime(0.35, t + 0.005)
    gain.gain.setValueAtTime(0.35, t + 0.055)
    gain.gain.linearRampToValueAtTime(0.0, t + 0.07)
    osc.start(t); osc.stop(t + 0.08)
  } catch {}
}

export function BarcodeScanner({
  onDetected,
  onClose,
  title = 'Scanner code-barres',
}: {
  onDetected: (code: string) => void
  onClose: () => void
  title?: string
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const stopRef = useRef<(() => void) | null>(null)
  const detectedRef = useRef(false)
  const [manual, setManual] = useState('')
  const [status, setStatus] = useState('Démarrage...')

  useEffect(() => {
    startScanner()
    return () => { stopRef.current?.() }
  }, [])

  async function startScanner() {
    if ('BarcodeDetector' in window) {
      await startNative()
    } else {
      await startZxing()
    }
  }

  // ── BarcodeDetector natif (Chrome récent) ──────────────────────────────────
  async function startNative() {
    try {
      const stream = await getStream()
      const detector = new (window as any).BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'qr_code'],
      })
      setStatus('Pointe vers le code-barres')
      let running = true
      stopRef.current = () => { running = false; stream.getTracks().forEach(t => t.stop()) }

      const scan = async () => {
        if (!running || !videoRef.current) return
        try {
          const codes = await detector.detect(videoRef.current)
          if (codes.length > 0) { return handleDetected(codes[0].rawValue, stream) }
        } catch {}
        requestAnimationFrame(scan)
      }
      requestAnimationFrame(scan)
    } catch {
      await startZxing()
    }
  }

  // ── ZXing — boucle RAF manuelle (bien plus rapide que decodeFromConstraints) ─
  async function startZxing() {
    try {
      setStatus('Démarrage caméra...')
      const stream = await getStream()
      setStatus('Pointe vers le code-barres')

      const { BrowserMultiFormatReader } = await import('@zxing/browser')
      const { DecodeHintType, BarcodeFormat } = await import('@zxing/library')

      const hints = new Map()
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
        BarcodeFormat.CODE_128, BarcodeFormat.CODE_39,
        BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
        BarcodeFormat.QR_CODE,
      ])
      hints.set(DecodeHintType.TRY_HARDER, true)

      const reader = new BrowserMultiFormatReader(hints)
      const canvas = canvasRef.current || document.createElement('canvas')

      let running = true
      stopRef.current = () => {
        running = false
        stream.getTracks().forEach(t => t.stop())
        try { (reader as any)?.reset?.() } catch {}
      }

      const scan = () => {
        if (!running) return
        const video = videoRef.current
        if (!video || video.videoWidth === 0 || video.readyState < 2) {
          requestAnimationFrame(scan)
          return
        }

        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!

        // Scan full frame
        ctx.drawImage(video, 0, 0)
        try {
          const result = reader.decodeFromCanvas(canvas)
          return handleDetected(result.getText(), stream, reader)
        } catch {}

        // Scan crop centre (meilleur pour EAN-13 — barcode horizontal)
        const cw = Math.round(video.videoWidth * 0.9)
        const ch = Math.round(video.videoHeight * 0.4)
        const cx = Math.round((video.videoWidth - cw) / 2)
        const cy = Math.round((video.videoHeight - ch) / 2)
        canvas.width = cw * 2
        canvas.height = ch * 2
        ctx.drawImage(video, cx, cy, cw, ch, 0, 0, cw * 2, ch * 2)
        try {
          const result = reader.decodeFromCanvas(canvas)
          return handleDetected(result.getText(), stream, reader)
        } catch {}

        requestAnimationFrame(scan)
      }

      requestAnimationFrame(scan)
    } catch (e: any) {
      setStatus('Erreur caméra — saisir manuellement')
    }
  }

  async function getStream() {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'environment',
        width: { ideal: 1920, min: 1280 },
        height: { ideal: 1080, min: 720 },
      },
    })
    if (videoRef.current) {
      videoRef.current.srcObject = stream
      await videoRef.current.play()
    }
    return stream
  }

  function handleDetected(code: string, stream: MediaStream, reader?: any) {
    if (detectedRef.current) return
    detectedRef.current = true
    stream.getTracks().forEach(t => t.stop())
    try { reader?.reset() } catch {}
    beep()
    onDetected(code)
  }

  function submitManual() {
    const v = manual.trim().toUpperCase()
    if (!v) return
    detectedRef.current = true
    stopRef.current?.()
    beep()
    onDetected(v)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-12 pb-3">
        <button onClick={onClose} className="text-white p-2">
          <X className="w-6 h-6" />
        </button>
        <p className="text-white font-semibold text-sm">{title}</p>
        <span className="w-10" />
      </div>

      {/* Viewfinder */}
      <div className="flex-1 relative overflow-hidden">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          muted
          playsInline
        />
        {/* Canvas caché pour le traitement */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Overlay sombre */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 right-0 bg-black/50" style={{ height: '30%' }} />
          <div className="absolute bottom-0 left-0 right-0 bg-black/50" style={{ height: '30%' }} />
          {/* Cadre scan — plus large pour EAN-13 */}
          <div className="absolute left-4 right-4" style={{ top: '30%', height: '40%' }}>
            <div className="absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 border-red-400 rounded-tl-lg" />
            <div className="absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 border-red-400 rounded-tr-lg" />
            <div className="absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 border-red-400 rounded-bl-lg" />
            <div className="absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 border-red-400 rounded-br-lg" />
            <div
              className="absolute inset-x-3 top-1/2 h-0.5 bg-red-400"
              style={{ boxShadow: '0 0 10px #f87171, 0 0 20px #f87171' }}
            />
          </div>
        </div>
        <p className="absolute bottom-4 left-0 right-0 text-center text-white/60 text-xs pointer-events-none">
          {status}
        </p>
      </div>

      {/* Saisie manuelle */}
      <div className="bg-gray-950 px-5 pt-4 pb-10 space-y-3">
        <p className="text-white/40 text-xs text-center">Ou saisir manuellement :</p>
        <div className="flex gap-2">
          <input
            value={manual}
            onChange={e => setManual(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && submitManual()}
            placeholder="Ex: 619123456789 ou CRDN570ZX"
            autoCapitalize="characters"
            className="flex-1 px-4 py-3.5 bg-white/10 text-white placeholder-white/25 rounded-2xl outline-none border border-white/15 font-mono text-sm"
          />
          <button
            onClick={submitManual}
            disabled={!manual.trim()}
            className="px-5 py-3 bg-indigo-600 text-white rounded-2xl font-bold text-sm disabled:opacity-40 active:scale-95"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  )
}
